import { useEffect } from 'react';
import { createResilientSubscription } from '../../utils/realtimeManager';
import { createDedupTracker } from './dedup';
import { ensureDigitalOrderNotification } from './digitalInsert';
import { playNotificationSound } from './sound';
import type { Order } from '../../types';
import type { UserSession } from './types';
import type { PendingOrdersApi } from './usePendingOrders';

export function useOrderSubscriptions(session: UserSession, pending: PendingOrdersApi) {
  const { user, userRole, assignedBrandIds } = session;
  const { setPendingNewOrders, setShowNewOrdersBanner } = pending;

  const ordersDedup = createDedupTracker();
  const digitalDedup = createDedupTracker();

  // Subscribe to new orders globally (so notifications work on any page)
  useEffect(() => {
    if (!user?.id || !userRole) return;

    let filter: string | undefined;
    if (userRole === 'Customer service' && user.id) {
      filter = `assignedCsId=eq.${user.id}`;
    }

    const sub = createResilientSubscription({
      channelName: `orders-channel-global-${user.id}`,
      event: 'INSERT',
      table: 'orders',
      filter,
      callback: async (payload: any) => {
        const newOrder = {
          ...payload.new,
          productId: payload.new.product_id ?? payload.new.productId ?? null,
          shippingResi: payload.new.shipping_resi ?? payload.new.shippingResi,
          courier: payload.new.courier,
          exportedAt: payload.new.exportedAt,
          exportedBy: payload.new.exportedBy,
        } as Order;

        // Client-side brand isolation for all non-Super Admin roles
        if (userRole !== 'Super Admin' && assignedBrandIds.length > 0) {
          const orderBrandId = newOrder.brandId;
          if (!orderBrandId || !assignedBrandIds.includes(orderBrandId)) {
            return;
          }
        }

        // Store in pending so OrdersPage can show the banner
        setPendingNewOrders(prev => {
          if (prev.some(o => o.id === newOrder.id)) return prev;
          return [newOrder, ...prev];
        });
        setShowNewOrdersBanner(true);

        if (ordersDedup.isDuplicate(`${user.id}:${newOrder.id}`)) return;

        // Beep langsung dari event order (tak nunggu baris notifikasi jadi).
        // Dedup 10 mnt di sound.ts cegah ganda dengan jalur notifikasi.
        playNotificationSound('ORDER_NEW', (newOrder as any).id);

        // Notification is created by create_order_notifications RPC (useFormViewer.ts)
        // This subscription only handles the pending orders banner, not duplicating notifications
      }
    });

    return () => {
      sub.unsubscribe();
    };
  }, [user?.id, userRole, assignedBrandIds.join(',')]);

  // Subscribe to digital_orders globally (so sound works on any page)
  useEffect(() => {
    if (!user?.id || !userRole) return;

    const sub = createResilientSubscription({
      channelName: `digital-orders-channel-global-${user.id}`,
      event: 'INSERT',
      table: 'digital_orders',
      callback: async (payload: any) => {
        const newOrder = payload.new as any;

        if (userRole !== 'Super Admin' && assignedBrandIds.length > 0) {
          if (!newOrder.brandId || !assignedBrandIds.includes(newOrder.brandId)) return;
        }

        if (digitalDedup.isDuplicate(`${user.id}:${newOrder.id}`)) return;

        // Beep langsung (ding) — tak nunggu insert notifikasi selesai.
        playNotificationSound('ORDER_NEW', newOrder.id, undefined, 'digital_order_new');

        await ensureDigitalOrderNotification(user.id, newOrder);
      }
    });

    return () => {
      sub.unsubscribe();
    };
  }, [user?.id, userRole, assignedBrandIds.join(',')]);
}
